import { useEffect } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Button, Col, Form, FormGroup, Input, Row } from 'reactstrap';
import { ProjectShowroomInfo } from '../models/investors';

type ShowroomInfoForm = Omit<ProjectShowroomInfo, 'image'>;

interface Props {
  projectId: string;
  showroomInfo: ShowroomInfoForm | null;
  handleForm: (form: ShowroomInfoForm) => void;
}

const schema = yup
  .object({
    badge: yup.string().optional(),
    title: yup.string().required('Requerido'),
    description: yup.string().required('Requerido'),
    address: yup.string().required('Requerido'),
    schedules: yup
      .array(
        yup.object({
          days: yup.string().required('Requerido'),
          start: yup.string().required('Requerido'),
          end: yup.string().required('Requerido'),
        }),
      )
      .min(1, 'Agrega al menos un horario')
      .required(),
    contact_name: yup.string().required('Requerido'),
    contact_whatsapp: yup.string().required('Requerido'),
  })
  .required();

const defaultSchedule = { days: '', start: '', end: '' };

const ProjectShowroomInfoForm = ({ showroomInfo, handleForm }: Props) => {
  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm<ShowroomInfoForm>({
    resolver: yupResolver(schema),
    defaultValues: {
      schedules: [defaultSchedule],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'schedules',
  });

  useEffect(() => {
    if (showroomInfo) {
      reset({
        badge: showroomInfo.badge ?? '',
        title: showroomInfo.title,
        description: showroomInfo.description,
        address: showroomInfo.address,
        schedules:
          showroomInfo.schedules?.length > 0
            ? showroomInfo.schedules
            : [defaultSchedule],
        contact_name: showroomInfo.contact_name,
        contact_whatsapp: showroomInfo.contact_whatsapp,
      });
    }
  }, [showroomInfo]);

  return (
    <Form role="form" onSubmit={handleSubmit(handleForm)}>
      <Row>
        <Col>
          <FormGroup>
            <label className="form-control-label" htmlFor="badge">
              Badge
            </label>
            <Controller
              name="badge"
              control={control}
              render={({ field }) => (
                <Input
                  type="text"
                  placeholder="Ej: Showroom abierto"
                  {...field}
                />
              )}
            />
          </FormGroup>

          <FormGroup>
            <label className="form-control-label" htmlFor="title">
              Título
            </label>
            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <Input
                  className={errors.title?.message ? 'is-invalid' : ''}
                  type="text"
                  placeholder="Ingresa el título"
                  {...field}
                />
              )}
            />
            <h5 className="text-danger">{errors.title?.message}</h5>
          </FormGroup>

          <FormGroup>
            <label className="form-control-label" htmlFor="description">
              Descripción
            </label>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <Input
                  className={errors.description?.message ? 'is-invalid' : ''}
                  type="textarea"
                  placeholder="Ingresa la descripción"
                  {...field}
                />
              )}
            />
            <h5 className="text-danger">{errors.description?.message}</h5>
          </FormGroup>

          <FormGroup>
            <label className="form-control-label" htmlFor="address">
              Dirección
            </label>
            <Controller
              name="address"
              control={control}
              render={({ field }) => (
                <Input
                  className={errors.address?.message ? 'is-invalid' : ''}
                  type="text"
                  placeholder="Ingresa la dirección"
                  {...field}
                />
              )}
            />
            <h5 className="text-danger">{errors.address?.message}</h5>
          </FormGroup>

          <h6 className="heading-small text-muted mb-3">Horarios</h6>
          {fields.map((field, index) => (
            <Row key={field.id} className="mb-2 align-items-end">
              <Col md="4">
                <FormGroup className="mb-0">
                  <label className="form-control-label">Días</label>
                  <Controller
                    name={`schedules.${index}.days`}
                    control={control}
                    render={({ field }) => (
                      <Input
                        type="text"
                        placeholder="Ej: Lun - Vie"
                        {...field}
                      />
                    )}
                  />
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup className="mb-0">
                  <label className="form-control-label">Inicio</label>
                  <Controller
                    name={`schedules.${index}.start`}
                    control={control}
                    render={({ field }) => (
                      <Input type="text" placeholder="Ej: 8:00" {...field} />
                    )}
                  />
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup className="mb-0">
                  <label className="form-control-label">Fin</label>
                  <Controller
                    name={`schedules.${index}.end`}
                    control={control}
                    render={({ field }) => (
                      <Input type="text" placeholder="Ej: 17:00" {...field} />
                    )}
                  />
                </FormGroup>
              </Col>
              <Col md="2">
                <Button
                  color="danger"
                  size="sm"
                  type="button"
                  onClick={() => remove(index)}
                  disabled={fields.length === 1}
                >
                  <i className="fa fa-trash" />
                </Button>
              </Col>
            </Row>
          ))}
          <Button
            color="secondary"
            size="sm"
            type="button"
            className="mb-4"
            onClick={() => append(defaultSchedule)}
          >
            <i className="fa fa-plus mr-1" /> Agregar horario
          </Button>
          {errors.schedules?.message && (
            <h5 className="text-danger">{errors.schedules.message}</h5>
          )}

          <FormGroup>
            <label className="form-control-label" htmlFor="contact_name">
              Nombre de contacto
            </label>
            <Controller
              name="contact_name"
              control={control}
              render={({ field }) => (
                <Input
                  className={errors.contact_name?.message ? 'is-invalid' : ''}
                  type="text"
                  placeholder="Ingresa el nombre de contacto"
                  {...field}
                />
              )}
            />
            <h5 className="text-danger">{errors.contact_name?.message}</h5>
          </FormGroup>

          <FormGroup>
            <label className="form-control-label" htmlFor="contact_whatsapp">
              WhatsApp de contacto
            </label>
            <Controller
              name="contact_whatsapp"
              control={control}
              render={({ field }) => (
                <Input
                  className={
                    errors.contact_whatsapp?.message ? 'is-invalid' : ''
                  }
                  type="text"
                  placeholder="Ej: +503 1234 5678"
                  {...field}
                />
              )}
            />
            <h5 className="text-danger">{errors.contact_whatsapp?.message}</h5>
          </FormGroup>

          <Button color="success">Guardar</Button>
        </Col>
      </Row>
    </Form>
  );
};

export default ProjectShowroomInfoForm;
